import React, { useState, FC, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { fetchArtworksByArtistId } from 'features/artworks/actions';
import { Slate } from 'components/Slate';
import { AtomicArtwork } from 'components/artworks/AtomicArtwork';
import { Container } from 'components/UI/Container';

const nextItemsOfArray = (items: number[], current: number, count: number) => {
  const index = items.indexOf(current);
  const withoutCurrent = items.filter((i) => i !== current);
  const result = withoutCurrent.slice(index, index + count);
  const missingLength = count - result.length;
  if (missingLength > 0) {
    const maxAvailableIds = withoutCurrent.length - result.length;
    const maxSlice = Math.min(maxAvailableIds, missingLength);
    return [...result, ...withoutCurrent.slice(0, maxSlice)];
  } else {
    return result;
  }
};

type Props = {
  artistId: number;
  artistSlug: string;
  currentArtworkId?: number;
  expectedCount?: number;
  maxCount: number;
};

export const ArtworksByArtistSlate: FC<Props> = ({
  artistId,
  artistSlug,
  currentArtworkId,
  /**
   * Displays `expectedCount` placeholders while
   * artworks are loading.
   */
  expectedCount = 6,
  /**
   * How many artworks to show.
   */
  maxCount,
}) => {
  const dispatch = useDispatch();
  const initialData: (number | undefined)[] = expectedCount
    ? Array(expectedCount).fill(undefined)
    : [];
  const [ids, setIds] = useState<(number | undefined)[] | undefined>(initialData);

  useEffect(() => {
    if (artistId) {
      const result = fetchArtworksByArtistId(artistId);
      result(dispatch, () => undefined, undefined).then((artworks) => {
        let ids = artworks.map((artwork) => artwork.id);
        if (currentArtworkId) {
          ids = nextItemsOfArray(ids, currentArtworkId, maxCount);
        }
        setIds(ids.length === 0 ? undefined : ids);
      });
    }
  }, [artistId]);

  if (ids === undefined || ids.length === 0) {
    return null;
  }

  return (
    <Container className='mb-3'>
      <Slate
        items={ids}
        renderItem={(artworkId, index) => (
          <AtomicArtwork artworkId={artworkId} cacheKey='artworks-by-artist' key={index} />
        )}
        title='Other artworks by artist'
        more={{
          title: 'All artist artworks',
          URL: Routes.artist_artworks_path(artistSlug),
        }}
      />
    </Container>
  );
};
